import { supabase } from '../../config/supabase';
import type { AdminMfaPhase } from '../../config/mfaPolicy';
import { UserRole } from '../../types/database';

export type MfaFactorRef = { id: string };
export type AuthenticatorAssuranceLevel = 'aal1' | 'aal2' | null;

const PRIVILEGED_ADMIN_ROLES = new Set<string>([UserRole.Admin, UserRole.Superadmin]);

export function isPrivilegedAdminRole(role: string | null | undefined): boolean {
  if (!role) return false;
  return PRIVILEGED_ADMIN_ROLES.has(role);
}

export function needsMfaEnrollment(
  role: string | null | undefined,
  phase: AdminMfaPhase,
  factors: MfaFactorRef[]
): boolean {
  if (!isPrivilegedAdminRole(role)) return false;
  if (phase === 'off') return false;
  if (factors.length > 0) return false;
  // Grace + zero factors: banner only — enrollment optional, not forced.
  if (phase === 'grace') return false;
  return phase === 'enforce';
}

export function needsMfaChallenge(
  role: string | null | undefined,
  phase: AdminMfaPhase,
  aal: AuthenticatorAssuranceLevel,
  factors: MfaFactorRef[]
): boolean {
  if (!isPrivilegedAdminRole(role)) return false;
  if (phase === 'off') return false;
  if (factors.length === 0) return false;
  if (aal === 'aal2') return false;
  return true;
}

export async function getAuthenticatorAssuranceLevel(): Promise<AuthenticatorAssuranceLevel> {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) {
    throw error;
  }
  return data?.currentLevel ?? null;
}

export async function listVerifiedTotpFactors(): Promise<MfaFactorRef[]> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) {
    throw error;
  }
  return (data?.totp ?? []).map((factor) => ({ id: factor.id }));
}

/** Thrown when the user already has a verified TOTP factor; UI should route to verify. */
export class MfaAlreadyEnrolledError extends Error {
  constructor() {
    super('MFA_ALREADY_ENROLLED');
    this.name = 'MfaAlreadyEnrolledError';
  }
}

export async function enrollTotp(): Promise<{
  factorId: string;
  qrCode: string;
  secret: string;
}> {
  // Force a server user fetch so listFactors sees Dashboard deletions / prior attempts.
  // Do not call refreshSession() here — it updates AuthContext and re-triggers enroll effects.
  await supabase.auth.getUser();

  const { data: factorsData, error: listError } = await supabase.auth.mfa.listFactors();
  if (listError) {
    throw listError;
  }

  // `totp` is verified-only; dangling enroll attempts live in `all` as unverified.
  const unverifiedTotpFactors = (factorsData?.all ?? []).filter(
    (factor) => factor.factor_type === 'totp' && factor.status === 'unverified'
  );
  for (const factor of unverifiedTotpFactors) {
    const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
    if (unenrollError) {
      throw unenrollError;
    }
  }

  if ((factorsData?.totp ?? []).length > 0) {
    throw new MfaAlreadyEnrolledError();
  }

  // Unique name avoids mfa_factor_name_conflict if a stale factor remains server-side.
  const friendlyName = `Authenticator app ${Date.now()}`;

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName,
  });
  if (error) {
    throw error;
  }
  if (!data?.id || !data.totp?.qr_code || !data.totp?.secret) {
    throw new Error('Unexpected MFA enroll response');
  }
  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
  };
}

export async function verifyTotpEnrollment(factorId: string, code: string): Promise<void> {
  const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
    factorId,
  });
  if (challengeError) {
    throw challengeError;
  }
  if (!challengeData?.id) {
    throw new Error('Unexpected MFA challenge response');
  }

  const { error: verifyError } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challengeData.id,
    code,
  });
  if (verifyError) {
    throw verifyError;
  }
}

export async function challengeAndVerifyTotp(factorId: string, code: string): Promise<void> {
  const { error } = await supabase.auth.mfa.challengeAndVerify({
    factorId,
    code,
  });
  if (error) {
    throw error;
  }
}
